import React, { lazy, Suspense } from 'react'
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router'
import {
  MutationCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { AnimatePresence } from 'framer-motion'

import { ErrorBoundary, LoadingSpinner } from './components/common'
import Layout from './components/layout/Layout'
import AiSecurityNotificationsProducer from './components/messaging/AiSecurityNotificationsProducer'
import ToastProvider from './components/messaging/ToastProvider'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import {
  FeatureFlagsProvider,
  useFeatureFlags,
} from './contexts/FeatureFlagsContext'
import { GlobalFilterProvider } from './contexts/GlobalFilterContext'
import { MessagingProvider } from './contexts/MessagingContext'
import { PageTimestampModeDefaultProvider } from './contexts/PageTimestampModeDefault'
import { TenantProvider } from './contexts/TenantContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { UserTierAssignmentsProvider } from './contexts/UserTierAssignmentsContext'
import { MuiThemeProvider } from './styles/MuiThemeProvider'

// Lazy load components
const Dashboard = lazy(() => import('./pages/overview/Dashboard'))
const NetworkInventory = lazy(
  () => import('./pages/infrastructure/network/NetworkInventory'),
)
const PlatformInventory = lazy(
  () => import('./pages/infrastructure/platform/PlatformInventory'),
)
const ComputeInventory = lazy(
  () => import('./pages/infrastructure/compute/ComputeInventory'),
)
const DataInventory = lazy(
  () => import('./pages/infrastructure/data/DataInventory'),
)
const ApplicationInventory = lazy(
  () => import('./pages/infrastructure/application/ApplicationMultiDashboard'),
)
const Settings = lazy(() => import('./pages/Settings'))

// Integration & Reporting pages
const SlackIntegration = lazy(
  () => import('./pages-wip/integrations/SlackIntegration'),
)
const Reports = lazy(() => import('./pages-wip/reporting/Reports'))
const Notifications = lazy(() => import('./pages-wip/alerts/Notifications'))
const NotificationTemplates = lazy(
  () => import('./pages-wip/alerts/NotificationTemplates'),
)
const JiraIntegration = lazy(() => import('./pages/integrations/jira'))
const ServiceNowIntegrationsDashboard = lazy(() =>
  import('./pages/integrations/servicenow').then(m => ({
    default: m.ServiceNowIntegrationsDashboard,
  })),
)
const HttpIntegration = lazy(() => import('./pages/integrations/http'))
const DefenderIntegration = lazy(() => import('./pages/integrations/defender'))
const DefenderConsentCallback = lazy(() =>
  import('./pages/integrations/defender').then(m => ({
    default: m.DefenderConsentCallback,
  })),
)
const CrowdStrikeIntegration = lazy(
  () => import('./pages/integrations/crowdstrike'),
)
const ExportCenter = lazy(() => import('./pages-wip/reporting/ExportCenter'))

// Admin pages
const UserManagement = lazy(
  () => import('./pages/administration/user-management/UserManagement'),
)
const APIKeyManagement = lazy(
  () => import('./pages/administration/api-keys/APIKeyManagement'),
)
// External API tokens: disabled with the external-api service (PENG-13718).
// Minting a token now fails with 404 because the API Gateway it provisioned
// against is gone. Left commented, along with its route below and the Sidebar
// entry, so restoring the page is an uncomment.
// const ExternalAPITokenManagement = lazy(
//   () =>
//     import('./pages/administration/external-api-tokens/ExternalAPITokenManagement'),
// )
const BusinessImpact = lazy(
  () => import('./pages/administration/business-impact/BusinessImpact'),
)
const OrganizationManagement = lazy(
  () => import('./pages/administration/org-management/OrganizationManagement'),
)
const CloudServices = lazy(
  () => import('./pages/administration/cloud-services/CloudServices'),
)
const RolePermissions = lazy(
  () => import('./pages-wip/administration/roles/RolePermissions'),
)
const TenantSettings = lazy(
  () => import('./pages/administration/tenants/TenantSettings'),
)
// ⚠️ THROWAWAY DEV HARNESS — remove with its route before the PR.
const ExhibitPanelHarness = lazy(
  () => import('./pages/dev/ExhibitPanelHarness'),
)
const TenantCloudServicesPage = lazy(
  () => import('./pages/administration/cloud-services/TenantCloudServicesPage'),
)
const AuditLogs = lazy(
  () => import('./pages-wip/administration/audit-logs/AuditLogs'),
)
const RuleManager = lazy(
  () => import('./pages/administration/rule-manager/RuleManager'),
)

const VulnerabilitiesDetail = lazy(
  () => import('./pages/risk/vulnerabilities-detail/Vulnerabilities'),
)

// Security Posture — tab shell wrapping Vulnerabilities, Misconfigurations, Sensitive Data
const SecurityFindingsPage = lazy(
  () => import('./pages/ai-applications/SecurityFindingsPage'),
)

// AI Applications pages
const CISOScorecardPage = lazy(
  () => import('./pages/ai-applications/CISOScorecardPage'),
)
const CompliancePage = lazy(
  () => import('./pages/ai-applications/CompliancePage'),
)
// The unified component-instance catalog replaces both the legacy type-level
// catalog and the standalone Shadow AI surface.
// The tab shell owns the shared header and lazily loads each tab's body, so
// the Endpoints grid costs nothing until that tab is opened.
const ComponentCatalogTabPage = lazy(
  () =>
    import('./pages/ai-applications/component-catalog/ComponentCatalogTabPage'),
)
// AI Security Concerns → Analyst Console. The tab shell (Types /
// Detections / Signals) was retired; the console renders three category
// queues on one surface. Legacy /types /detections /signals sub-routes
// redirect to the base route below so existing deep links keep working.
const AnalystConsolePage = lazy(
  () => import('./pages/ai-applications/concerns/AnalystConsolePage'),
)
const IncidentsPage = lazy(
  () => import('./pages/ai-applications/IncidentsPage'),
)

// Development pages
const ComponentDemo = lazy(() => import('./pages/demos/ComponentDemo'))
const CellsDemo = lazy(() => import('./pages/demos/CellsDemo'))
const CardsDemo = lazy(() => import('./pages/demos/CardsDemo'))
const IconsDemo = lazy(() => import('./pages/demos/IconsDemo'))
const GcpSurfacesDemo = lazy(() => import('./pages/demos/GcpSurfacesDemo'))
const DocsPortal = lazy(() => import('./pages/docs'))

// AI Security pages
const AIApplicationsPage = lazy(
  () => import('./pages/ai-security/applications/AIApplicationsPage'),
)
const AIApplicationDetailPage = lazy(
  () => import('./pages/ai-security/applications/AIApplicationDetailPage'),
)
const AIWorkloadsPage = lazy(
  () => import('./pages/ai-security/workloads/AIWorkloadsPage'),
)
// const AIIdentitiesPage = lazy(
//   () => import('./pages/ai-security/identities/AIIdentitiesPage'),
// )
const AIIdentitiesTabPage = lazy(
  () => import('./pages/ai-security/identities/AIIdentitiesTabPage'),
)
const AIDetectionRulesPage = lazy(
  () => import('./pages/ai-security/detection-rules/AIDetectionRulesPage'),
)

// Demo / test pages
const GraphTest = lazy(() => import('./pages/demos/GraphTest'))
const IdentityRiskWalkthrough = lazy(
  () => import('./pages/demos/IdentityRiskWalkthrough'),
)

// Auth pages
const Logout = lazy(() => import('./pages/Logout'))

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },

  // Invalidate queries when a mutation completes successfully. This ensures
  // that the user won't see stale data after a mutation because the query data
  // was still alive in the cache. If a mutation key is not specified, all
  // queries in the cache are invalidated, which is a safe fallback behavior.
  //
  // See the link below for a description of the problem and various solutions.
  // Another option would be to export queryClient and have each mutation
  // implement invalidation.
  //
  // https://tkdodo.eu/blog/automatic-query-invalidation-after-mutations
  mutationCache: new MutationCache({
    onSuccess: (_data, _vars, _context, mutation) => {
      queryClient.invalidateQueries({
        queryKey: mutation.options.mutationKey,
      })
    },
  }),
})

// Loading component
const AppLoadingSpinner = () => (
  <div className="h-64">
    <LoadingSpinner message="" fullHeight />
  </div>
)

// Protected Route wrapper with optional role-based access control
const ProtectedRoute = ({
  children,
  requiredRoles,
}: {
  children: React.JSX.Element
  requiredRoles?: string[]
}) => {
  const { isAuthenticated, isLoginDisabled, login, isInitialized, user } =
    useAuth()
  const [isRedirecting, setIsRedirecting] = React.useState(false)
  const navigate = useNavigate()

  // Trigger OAuth login redirect when not authenticated
  // Must be at the top before any conditional returns (React Hooks rules)
  React.useEffect(() => {
    if (isInitialized && !isLoginDisabled && !isAuthenticated) {
      setIsRedirecting(true)
      login()
    }
  }, [isInitialized, isLoginDisabled, isAuthenticated, login])

  // Check role-based access if requiredRoles is specified (using same pattern as Sidebar)
  const hasRequiredRole =
    !requiredRoles ||
    requiredRoles.length === 0 ||
    user?.groups?.some(
      group =>
        group.metadata?.role === 'admin' ||
        group.metadata?.role === 'super_admin',
    )

  // Redirect to dashboard if user doesn't have required role
  // Must be called before any conditional returns (React Hooks rules)
  React.useEffect(() => {
    if (
      isAuthenticated &&
      requiredRoles &&
      requiredRoles.length > 0 &&
      !hasRequiredRole
    ) {
      navigate('/dashboard')
    }
  }, [isAuthenticated, requiredRoles, hasRequiredRole, navigate])

  // Wait for auth initialization
  if (!isInitialized) {
    return <AppLoadingSpinner />
  }

  // If login is disabled (demo mode), allow access
  if (isLoginDisabled) {
    return children
  }

  // Show loading spinner while redirecting to OAuth or if already redirecting
  if (!isAuthenticated || isRedirecting) {
    return <AppLoadingSpinner />
  }

  // Show loading while redirecting
  if (requiredRoles && requiredRoles.length > 0 && !hasRequiredRole) {
    return <AppLoadingSpinner />
  }

  return children
}

// Redirect /ai-security/applications/:id → /ai-applications/catalog/:id
const ApplicationIdRedirect = () => {
  const { id } = useParams()
  return <Navigate to={`/ai-applications/catalog/${id ?? ''}`} replace />
}

// Animated Routes component
const AnimatedRoutes = () => {
  const location = useLocation()
  const { isFeatureEnabled, isDevelopmentMode } = useFeatureFlags()

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        {/* Public Routes */}
        <Route path="/logout" element={<Logout />} />

        {/* Protected Routes */}
        <Route
          path="/"
          element={<Navigate to="/ai-applications/catalog" replace />}
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/inventory"
          element={<Navigate to="/inventory/network" replace />}
        />
        <Route
          path="/inventory/network"
          element={
            <ProtectedRoute>
              <NetworkInventory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/inventory/platform"
          element={
            <ProtectedRoute>
              <PlatformInventory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/inventory/compute"
          element={
            <ProtectedRoute>
              <ComputeInventory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/inventory/data"
          element={
            <ProtectedRoute>
              <DataInventory />
            </ProtectedRoute>
          }
        />
        {/* Application Multi-Grid Routes */}
        <Route
          path="/inventory/application"
          element={
            <ProtectedRoute>
              <ApplicationInventory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/inventory/application/sites"
          element={
            <ProtectedRoute>
              <ApplicationInventory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/inventory/application/endpoints"
          element={
            <ProtectedRoute>
              <ApplicationInventory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/inventory/application/images"
          element={
            <ProtectedRoute>
              <ApplicationInventory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          }
        />

        {/* Integration & Reporting Routes */}
        <Route
          path="/integrations/slack"
          element={
            <ProtectedRoute>
              <SlackIntegration />
            </ProtectedRoute>
          }
        />
        <Route
          path="/integrations/defender"
          element={
            <ProtectedRoute>
              <DefenderIntegration />
            </ProtectedRoute>
          }
        />
        {/* Fixed path, matching the redirect URI registered on the Defender app
            registration -- Entra permits no wildcards, so the tenant travels in
            the state nonce rather than the URL. */}
        <Route
          path="/integrations/defender/callback"
          element={
            <ProtectedRoute>
              <DefenderConsentCallback />
            </ProtectedRoute>
          }
        />
        <Route
          path="/integrations/crowdstrike"
          element={
            <ProtectedRoute>
              <CrowdStrikeIntegration />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports"
          element={
            <ProtectedRoute>
              <Reports />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <Notifications />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notification-templates"
          element={
            <ProtectedRoute>
              <NotificationTemplates />
            </ProtectedRoute>
          }
        />
        <Route
          path="/integrations/jira"
          element={
            <ProtectedRoute>
              <JiraIntegration />
            </ProtectedRoute>
          }
        />
        {/* SNOW Integration: route added */}
        <Route
          path="/integrations/servicenow"
          element={
            <ProtectedRoute>
              <ServiceNowIntegrationsDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/integrations/http"
          element={
            <ProtectedRoute>
              <HttpIntegration />
            </ProtectedRoute>
          }
        />
        <Route
          path="/export-center"
          element={
            <ProtectedRoute>
              <ExportCenter />
            </ProtectedRoute>
          }
        />

        {/* Legacy redirect routes — old paths forward to new /ai-applications structure */}
        <Route
          path="/risk"
          element={
            <Navigate to="/ai-applications/findings/vulnerabilities" replace />
          }
        />
        <Route
          path="/risk/vulnerabilities"
          element={
            <Navigate to="/ai-applications/findings/vulnerabilities" replace />
          }
        />
        <Route
          path="/ai-applications/findings/vulnerabilities-detail"
          element={
            <ProtectedRoute>
              <VulnerabilitiesDetail />
            </ProtectedRoute>
          }
        />
        {/* Legacy redirect — old /risk path forwards to the new
            /ai-applications/findings pattern, preserving query params so
            deep-links (?severity=, ?businessImpact=, ?provider=, …) keep working. */}
        <Route
          path="/risk/vulnerabilities-detail"
          element={
            <Navigate
              to={`/ai-applications/findings/vulnerabilities-detail${location.search}`}
              replace
            />
          }
        />
        <Route
          path="/risk/misconfigurations"
          element={
            <Navigate
              to="/ai-applications/findings/misconfigurations"
              replace
            />
          }
        />
        <Route
          path="/risk/sensitive-data"
          element={
            <Navigate to="/ai-applications/findings/sensitive-data" replace />
          }
        />
        {/* Legacy /security/* redirects — incidents-old (the tabbed
            Events + Incidents shell) has been removed (seccompose
            migration spec §5 step 1). Events → the Signals tab, which
            replaces the old events page; Incidents → the AI incidents
            page. */}
        <Route
          path="/security"
          element={
            <Navigate to="/ai-applications/security-concerns/signals" replace />
          }
        />
        <Route
          path="/security/events"
          element={
            <Navigate to="/ai-applications/security-concerns/signals" replace />
          }
        />
        <Route
          path="/security/incidents"
          element={<Navigate to="/ai-applications/incidents" replace />}
        />

        {/* Admin Routes */}
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute requiredRoles={['admin', 'super_admin']}>
              <UserManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/api-keys"
          element={
            <ProtectedRoute requiredRoles={['admin', 'super_admin']}>
              <APIKeyManagement />
            </ProtectedRoute>
          }
        />
        {/* Disabled with the external-api service (PENG-13718) -- see the
            ExternalAPITokenManagement import above. */}
        {/* <Route
          path="/admin/external-api-tokens"
          element={
            <ProtectedRoute requiredRoles={['admin', 'super_admin']}>
              <ExternalAPITokenManagement />
            </ProtectedRoute>
          }
        /> */}
        <Route
          path="/admin/business-impact"
          element={
            <ProtectedRoute>
              <BusinessImpact />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/org-management"
          element={
            <ProtectedRoute>
              <OrganizationManagement />
            </ProtectedRoute>
          }
        />
        {/* Global Admin Cloud Services - Feature Flagged for Phase Future */}
        <Route
          path="/admin/global-cloud-services"
          element={
            <ProtectedRoute>
              <CloudServices />
            </ProtectedRoute>
          }
        />
        {/* Tenant-Scoped Cloud Services - Available to admin + super_admin */}
        <Route
          path="/admin/cloud-services"
          element={
            <ProtectedRoute>
              <TenantCloudServicesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/roles"
          element={
            <ProtectedRoute>
              <RolePermissions />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/tenant"
          element={
            <ProtectedRoute>
              <TenantSettings />
            </ProtectedRoute>
          }
        />
        {/* ⚠️ THROWAWAY DEV HARNESS (see ExhibitPanelHarness.tsx) — remove
            this route and that file before opening the PR. Dev-server only:
            import.meta.env.DEV is false in any built bundle, so this never
            reaches a deployed app. */}
        {import.meta.env.DEV && (
          <Route path="/dev/exhibit" element={<ExhibitPanelHarness />} />
        )}
        <Route
          path="/admin/audit"
          element={
            <ProtectedRoute>
              <AuditLogs />
            </ProtectedRoute>
          }
        />
        {/* Rule Manager — gated by the 'rule-manager' feature flag so direct
            URL access is blocked when the flag is off, mirroring the sidebar. */}
        <Route
          path="/admin/rules"
          element={
            isFeatureEnabled('rule-manager') ? (
              <ProtectedRoute>
                <Suspense fallback={<LoadingSpinner />}>
                  <RuleManager />
                </Suspense>
              </ProtectedRoute>
            ) : (
              <Navigate to="/ai-applications/catalog" replace />
            )
          }
        />

        {/* Development Routes */}
        <Route
          path="/component-demo"
          element={
            <ProtectedRoute>
              <ComponentDemo />
            </ProtectedRoute>
          }
        />
        {/* GCP surfaces demo — mirrors the sidebar: both Development Mode and
            the gcp-surfaces-demo flag must be on, otherwise direct URL access
            is redirected. */}
        <Route
          path="/gcp-surfaces-demo"
          element={
            isDevelopmentMode() && isFeatureEnabled('gcp-surfaces-demo') ? (
              <ProtectedRoute>
                <GcpSurfacesDemo />
              </ProtectedRoute>
            ) : (
              <Navigate to="/ai-applications/catalog" replace />
            )
          }
        />
        <Route
          path="/gcp-surfaces-demo"
          element={
            <ProtectedRoute>
              <GcpSurfacesDemo />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cells-demo"
          element={
            <ProtectedRoute>
              <CellsDemo />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cards-demo"
          element={
            <ProtectedRoute>
              <CardsDemo />
            </ProtectedRoute>
          }
        />
        <Route
          path="/icons-demo"
          element={
            <ProtectedRoute>
              <IconsDemo />
            </ProtectedRoute>
          }
        />
        <Route
          path="/docs/*"
          element={
            <ProtectedRoute>
              <DocsPortal />
            </ProtectedRoute>
          }
        />
        {/* Legacy AI Security redirect routes */}
        <Route
          path="/ai-security/applications"
          element={<Navigate to="/ai-applications/catalog" replace />}
        />
        <Route
          path="/ai-security/applications/:id"
          element={<ApplicationIdRedirect />}
        />
        <Route
          path="/ai-security/workloads"
          element={
            <ProtectedRoute>
              <AIWorkloadsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-security/identities"
          element={<Navigate to="/ai-applications/identities" replace />}
        />
        <Route
          path="/ai-security/detection-rules"
          element={<Navigate to="/ai-applications/detection-rules" replace />}
        />

        {/* AI Applications routes */}
        <Route
          path="/ai-applications"
          element={<Navigate to="/ai-applications/catalog" replace />}
        />
        <Route
          path="/ai-applications/catalog"
          element={
            <ProtectedRoute>
              <AIApplicationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-applications/catalog/:id"
          element={
            <ProtectedRoute>
              <AIApplicationDetailPage />
            </ProtectedRoute>
          }
        />
        {/* Preserve the legacy catalog URL as a bookmark-safe alias. */}
        <Route
          path="/ai-applications/components"
          element={
            <Navigate
              to={`/ai-applications/component-catalog${location.search}`}
              replace
            />
          }
        />
        {/* The unified catalog is the default component inventory.

            The bare path stays the Components tab's OWN route rather than
            redirecting to a child: /ai-applications/shadow-ai redirects here
            with ?status=shadow, and the e2e suite asserts this exact URL. Both
            routes render the same shell, which picks the tab from the path. */}
        <Route
          path="/ai-applications/component-catalog"
          element={
            <ProtectedRoute>
              <ComponentCatalogTabPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-applications/component-catalog/endpoints"
          element={
            <ProtectedRoute>
              <ComponentCatalogTabPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-applications/identities"
          element={
            <Navigate to="/ai-applications/identities/identities" replace />
          }
        />
        <Route
          path="/ai-applications/identities/identities"
          element={
            <ProtectedRoute>
              <AIIdentitiesTabPage />
            </ProtectedRoute>
          }
        />
        {/* Identity detail — nested id route under the same
            AIIdentitiesTabPage element so the "Identities" tab detector
            (pathname.startsWith) stays active. IdentitiesRiskView
            reads `identityId` from useParams to swap list → detail. */}
        <Route
          path="/ai-applications/identities/identities/:identityId"
          element={
            <ProtectedRoute>
              <AIIdentitiesTabPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-applications/identities/runtime-usage"
          element={
            <ProtectedRoute>
              <AIIdentitiesTabPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-applications/identities/identity-path"
          element={
            <ProtectedRoute>
              <AIIdentitiesTabPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-applications/scorecard"
          element={
            <ProtectedRoute>
              <CISOScorecardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-applications/compliance"
          element={
            <ProtectedRoute>
              <CompliancePage />
            </ProtectedRoute>
          }
        />
        {/* Shadow AI is an ownership-status view of the unified catalog. */}
        <Route
          path="/ai-applications/shadow-ai"
          element={
            <Navigate
              to="/ai-applications/component-catalog?status=shadow"
              replace
            />
          }
        />
        {/* Incidents — sidebar Respond & Contain → Incidents. The
            previous AI Security Findings shell (tabbed Events +
            Incidents, formerly archived at /incidents-old) has been
            removed (seccompose migration spec §5 step 1). */}
        <Route
          path="/ai-applications/incidents"
          element={
            <ProtectedRoute>
              <IncidentsPage />
            </ProtectedRoute>
          }
        />
        {/* Analyst Console — the AI Security Concerns surface. Legacy
            tab sub-routes (/types /detections /signals) redirect to the
            base route so deep links keep working. */}
        <Route
          path="/ai-applications/security-concerns"
          element={
            <ProtectedRoute>
              <AnalystConsolePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-applications/security-concerns/types"
          element={
            <Navigate
              to={`/ai-applications/security-concerns${location.search}`}
              replace
            />
          }
        />
        <Route
          path="/ai-applications/security-concerns/detections"
          element={
            <Navigate
              to={`/ai-applications/security-concerns${location.search}`}
              replace
            />
          }
        />
        <Route
          path="/ai-applications/security-concerns/signals"
          element={
            <Navigate
              to={`/ai-applications/security-concerns${location.search}`}
              replace
            />
          }
        />
        <Route
          path="/ai-applications/findings"
          element={
            <Navigate to="/ai-applications/findings/vulnerabilities" replace />
          }
        />
        <Route
          path="/ai-applications/findings/events"
          element={
            <Navigate to="/ai-applications/findings/vulnerabilities" replace />
          }
        />
        <Route
          path="/ai-applications/findings/vulnerabilities"
          element={
            <ProtectedRoute>
              <SecurityFindingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-applications/findings/misconfigurations"
          element={
            <ProtectedRoute>
              <SecurityFindingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-applications/findings/sensitive-data"
          element={
            <ProtectedRoute>
              <SecurityFindingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-applications/detection-rules"
          element={
            <ProtectedRoute>
              <AIDetectionRulesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/graphtest"
          element={
            <ProtectedRoute>
              <GraphTest />
            </ProtectedRoute>
          }
        />
        <Route
          path="/identity-risk-walkthrough"
          element={
            <ProtectedRoute>
              <IdentityRiskWalkthrough />
            </ProtectedRoute>
          }
        />
        {/* Catch-all — unknown paths land on AI Applications */}
        <Route
          path="*"
          element={<Navigate to="/ai-applications/catalog" replace />}
        />
      </Routes>
    </AnimatePresence>
  )
}

// Conditional ReactQuery DevTools Component
const ConditionalReactQueryDevtools = () => {
  const { isReactQueryDevToolsEnabled } = useFeatureFlags()

  if (!isReactQueryDevToolsEnabled()) {
    return null
  }

  return <ReactQueryDevtools initialIsOpen={false} />
}

// Authenticated App Component
const AuthenticatedApp = () => {
  const { isAuthenticated, isInitialized, isLoginDisabled, login } = useAuth()
  const location = useLocation()

  // Check if current route should be shown without layout
  const isPublicRoute = location.pathname === '/logout'

  // If not authenticated and login is required, trigger redirect and show loading spinner
  // MUST be at top before any conditional returns (React Hooks rules)
  React.useEffect(() => {
    // Only trigger redirect if initialized, not on public routes, and not authenticated
    if (
      isInitialized &&
      !isPublicRoute &&
      !isAuthenticated &&
      !isLoginDisabled
    ) {
      login()
    }
  }, [isInitialized, isPublicRoute, isAuthenticated, isLoginDisabled, login])

  // Show loading spinner during initialization
  if (!isInitialized) {
    return <AppLoadingSpinner />
  }

  // With OAuth/Hosted UI, there are no public routes in the app
  // Cognito handles all authentication pages externally

  // Public routes (like logout page) should render without layout
  if (isPublicRoute) {
    return (
      <Suspense fallback={<AppLoadingSpinner />}>
        <AnimatedRoutes />
      </Suspense>
    )
  }

  // If login is disabled (demo mode), show without layout when not authenticated
  if (isLoginDisabled && !isAuthenticated) {
    return (
      <Suspense fallback={<AppLoadingSpinner />}>
        <AnimatedRoutes />
      </Suspense>
    )
  }

  if (!isAuthenticated && !isLoginDisabled) {
    return <AppLoadingSpinner />
  }

  // For authenticated users, show with layout
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 transition-colors">
      <Layout>
        <Suspense fallback={<AppLoadingSpinner />}>
          <AnimatedRoutes />
        </Suspense>
      </Layout>
    </div>
  )
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <FeatureFlagsProvider>
          <UserTierAssignmentsProvider>
            <ThemeProvider>
              <TenantProvider>
                <Router>
                  <GlobalFilterProvider>
                    {/* PENG-14090 — carries page-level "Filter by" defaults
                        (currently just the Component Catalog's 'first_seen')
                        into the shared time-filter chrome without mutating
                        the global timestampMode. See PageTimestampModeDefault.tsx. */}
                    <PageTimestampModeDefaultProvider>
                      <MuiThemeProvider>
                        <MessagingProvider>
                          {/* Headless feed for the notification bell. Must be
                              inside MessagingProvider to write to the bell, and
                              behind an ErrorBoundary so it can never take the app
                              down. */}
                          <ErrorBoundary label="AiSecurityNotificationsProducer">
                            <AiSecurityNotificationsProducer />
                          </ErrorBoundary>
                          <AuthenticatedApp />
                          <ToastProvider />
                        </MessagingProvider>
                      </MuiThemeProvider>
                    </PageTimestampModeDefaultProvider>
                  </GlobalFilterProvider>
                </Router>
              </TenantProvider>
            </ThemeProvider>
          </UserTierAssignmentsProvider>
          <ConditionalReactQueryDevtools />
        </FeatureFlagsProvider>
      </AuthProvider>
    </QueryClientProvider>
  )
}

export default App
